import { useRef } from "react";
import { useTimelinePlayback, useVideoPlayback } from "./hooks";
import { TimelinePlaybackState } from "./types";
import { useViewSettings } from "@/view-settings";
import { ListenerFn, ObservableListenerDispose, batch } from "@legendapp/state";

export const useSyncVideo = () => {
  const isInitialised = useRef(false);
  const timelinePlayback = useTimelinePlayback();
  const videoPlayback = useVideoPlayback();
  const dashPlayer = videoPlayback.dashPlayer;
  const viewSettings = useViewSettings();

  const prevPlaybackState = useRef<TimelinePlaybackState>("idle");
  const changeHandlerRef = useRef<ObservableListenerDispose | null>(null);

  const onPlaybackStateChange: ListenerFn<TimelinePlaybackState> = ({
    value,
    getPrevious,
  }) => {
    const currentSceneState = value;
    const isEnabled = viewSettings.showVideo.peek();
    const error = videoPlayback.videoContext.error.peek();
    const isVideoStandalone = videoPlayback.videoContext.isStandalone.peek();
    const canPlayVideo = isEnabled && !error;

    if (!dashPlayer || !canPlayVideo || isVideoStandalone) return;

    // Pause video when rendering on hold
    if (currentSceneState === "pending") {
      try {
        dashPlayer.off("playbackPaused", onPaused);
        dashPlayer.pause();
      } catch (err) {
        console.log(err);
      }
      setTimeout(() => {
        dashPlayer.on("playbackPaused", onPaused);
      });
    }

    const prev = getPrevious();

    // Continue video when rendering starts playing again
    if (prev === "pending" && currentSceneState === "playing") {
      try {
        dashPlayer.off("playbackPlaying", onPlaying);
        dashPlayer.play();
      } catch (err) {
        console.log(err);
      }
      setTimeout(() => {
        dashPlayer.on("playbackPlaying", onPlaying);
      });
    }

    prevPlaybackState.current = currentSceneState;
  };

  const onPlaying = () => {
    console.log("onPlaying");

    batch(() => {
      timelinePlayback.playbackState.set("playing");
      timelinePlayback.playbackLoadingContext.set(null);
    });
  };

  const onPaused = () => {
    console.log("onPaused");

    timelinePlayback.playbackState.set("paused");
  };

  const onWaiting = () => {
    console.log("onWaiting");

    batch(() => {
      timelinePlayback.playbackState.set("pending");
      timelinePlayback.playbackLoadingContext.set("video-pending");
    });
  };

  const onStalled = () => {
    console.log("onStalled");

    timelinePlayback.playbackState.set("paused");
  };
  const onError = () => {
    console.log("onError");

    timelinePlayback.playbackState.set("paused");
  };

  const onCanPlay = () => {
    console.log("onCanPlay");
  };

  const on = () => {
    if (isInitialised.current) {
      return;
    }

    dashPlayer.on("playbackPlaying", onPlaying);
    dashPlayer.on("playbackPaused", onPaused);
    dashPlayer.on("playbackWaiting", onWaiting);
    dashPlayer.on("playbackStalled", onStalled);
    dashPlayer.on("playbackError", onError);
    dashPlayer.on("canPlay", onCanPlay);

    const dispose = timelinePlayback.playbackState.onChange(
      onPlaybackStateChange
    );
    changeHandlerRef.current = dispose;

    if (!isInitialised.current) {
      isInitialised.current = true;
    }
  };

  const off = () => {
    if (!isInitialised.current) return;

    dashPlayer.off("playbackPlaying", onPlaying);
    dashPlayer.off("playbackPaused", onPaused);
    dashPlayer.off("playbackWaiting", onWaiting);
    dashPlayer.off("playbackStalled", onStalled);
    dashPlayer.off("playbackError", onError);
    dashPlayer.off("canPlay", onCanPlay);
    changeHandlerRef.current?.();

    isInitialised.current = false;
  };

  return { on, off };
};
